import React, { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

interface Props {
    /** Expanded to the panel width when true, collapsed to 0 when false. */
    open: boolean;
    /**
     * Takeover: the panel fills its container (the whole meeting area) instead
     * of the fixed side-panel width. Used whenever the main video is hidden —
     * the narrow window or the side-pane dock — so the panel never overflows a
     * container narrower than the side-panel width, and gets a symmetric frame.
     */
    fill?: boolean;
    /** Appearance of the content area (background, border, flex-direction…). */
    className?: string;
    children: ReactNode;
}

/**
 * A right-side meeting panel column (participants / chat). Always mounted; the
 * outer column's flex-basis slides between the panel width and 0, so the video
 * content re-measures and reflows beside it as it opens/closes.
 *
 * In the normal (beside-the-video) layout the content area keeps a FIXED width
 * throughout the slide — the column clips it via overflow-hidden — so it wipes
 * into view rather than being squeezed narrow and re-wrapping. When `fill` is
 * set the panel instead spans its container (see the prop), letting Tailwind
 * size it to the narrow window or the dock without any width arithmetic.
 */
const MeetingSidePanel = ({ open, fill, className, children }: Props) => {
    const openBasis = fill
        ? 'basis-full'
        : 'basis-(--spacing-meeting-side-panel)';

    return (
        <div
            className={twMerge(
                'meeting-panel-transition z-10 flex shrink-0 grow-0 overflow-hidden',
                open ? openBasis : 'basis-0',
            )}
        >
            <div
                className={twMerge(
                    'flex h-full shrink-0',
                    fill ? 'w-full' : 'w-(--spacing-meeting-side-panel)',
                    className,
                    // Takeover: the panel owns the whole area, so frame its
                    // content with a symmetric 12px on every side (overrides the
                    // caller's wide framing).
                    fill && 'p-3',
                )}
            >
                {children}
            </div>
        </div>
    );
};

export default MeetingSidePanel;
